import { type RefObject, useLayoutEffect, useState } from 'react';

import { instantOf } from '../../ActionHistory/utils';
import { logEntryElementId } from '../../utils';
import type { LogEntryModel } from '../types';

import type { StageChapter } from './types';
import { activeChapterIndex, firstIndexBelow } from './utils';

/**
 * The index of the chapter the reader is in, by the entry at the top of the log's view — in either order, so the
 * rail names the chapter of the entry the reader's eye starts from, as a running header does.
 *
 * Read again when the chapters or the entries change, before the browser paints, and in the frame after each scroll:
 * by then the log has read the same scroll for whether it left the reader at its newest end, whichever of the two
 * the browser told first. -1 while no chapter has begun by the top entry.
 *
 * A chapter whose entries are still being read in is the one the reader is in, whatever is at the top, and once it is
 * not, the top entry is read again before the browser paints.
 */
export const useActiveChapter = (
  scrollerRef: RefObject<HTMLElement>,
  /** The entries the log draws, in the order it draws them. */
  entries: LogEntryModel[],
  chapters: StageChapter[],
  isAtNewestEnd: () => boolean,
  pendingChapterKey?: string
): number => {
  const [activeIndex, setActiveIndex] = useState(-1);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    // With no chapters there is no rail; one drawn again later opens on none rather than the chapter it left.
    if (!scroller || chapters.length === 0) {
      setActiveIndex(-1);
      return undefined;
    }

    const readActiveChapter = () => {
      const viewTop = scroller.getBoundingClientRect().top;
      // Every entry the log draws is in the DOM under its id; one that is not counts as above the view.
      const bottomOf = (index: number): number =>
        document.getElementById(logEntryElementId(entries[index].notification.event_id))?.getBoundingClientRect()
          .bottom ?? Number.NEGATIVE_INFINITY;
      const topEntry = entries[firstIndexBelow(entries.length, bottomOf, viewTop)];

      setActiveIndex(
        activeChapterIndex(chapters, topEntry && instantOf(topEntry.notification.timestamp), isAtNewestEnd())
      );
    };

    let frame: number | undefined;
    const readOnNextFrame = () => {
      if (frame !== undefined) return;
      frame = requestAnimationFrame(() => {
        frame = undefined;
        readActiveChapter();
      });
    };

    readActiveChapter();
    scroller.addEventListener('scroll', readOnNextFrame, { passive: true });

    return () => {
      scroller.removeEventListener('scroll', readOnNextFrame);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [scrollerRef, entries, chapters, isAtNewestEnd, pendingChapterKey]);

  const pendingIndex = chapters.findIndex(({ key }) => key === pendingChapterKey);

  return pendingIndex === -1 ? activeIndex : pendingIndex;
};
